<template>
  <section class="hero-section" aria-labelledby="hero-title">
    <div class="hero-background"></div>
    <div class="hero-grid">
      <!-- Left Column: Text Content -->
      <div class="hero-text-col">
      <div class="hero-badge">
        <!-- Dark mode shield icon -->
        <img
          v-if="isDarkTheme"
          class="badge-icon"
          src="@/assets/icons/icon-Shield-darkmode.svg"
          alt="Shield check icon"
          width="16"
          height="16"
          loading="eager"
          decoding="async"
          aria-hidden="true"
        />
        <!-- Light mode shield icon -->
        <img
          v-else
          class="badge-icon"
          src="@/assets/icons/icon-Shield-lightmode.svg"
          alt="Shield check icon"
          width="16"
          height="16"
          loading="eager"
          decoding="async"
          aria-hidden="true"
        />
        <span>IS Code Compliant</span>
      </div>

      <h1 id="hero-title" class="hero-title">
        <span>Design Faster. </span>
        <span class="title-highlight">Verify Everything.</span>
        <span> Sleep better.</span>
      </h1>

      <div class="hero-subtitle">
        IS-compliant structural software built for step-by-step verification and documented results.
      </div>

      <div class="hero-actions">
        <div class="hero-cta-wrapper">
          <se-button
            variant="primary"
            style-set="set5"
            @click="navigateToSlabDesign"
          >
            Start Designing
            <!-- Dark mode arrow icon -->
            <img
              v-if="isDarkTheme"
              class="icon-arrow"
              src="@/assets/icons/icon-rightarrow-darkmode.svg"
              alt="Arrow icon"
              width="21"
              height="21"
              loading="eager"
              decoding="async"
              aria-hidden="true"
            />
            <!-- Light mode arrow icon -->
            <img
              v-else
              class="icon-arrow"
              src="@/assets/icons/icon-rightarrow-lightmode.svg"
              alt="Arrow icon"
              width="21"
              height="21"
              loading="eager"
              decoding="async"
              aria-hidden="true"
            />
          </se-button>
        </div>

        <div class="secondary-cta">
          <se-button
            variant="text"
            style-set="set5"
            class="secondary-cta-btn"
            @click="navigateToCalculators"
          >
            Explore calculators →
          </se-button>
        </div>
      </div>

      <!-- Testimonials slot: swap placeholder for real quotes when collected -->
      <div class="social-proof">
        <div class="social-proof-avatars">
          <div class="avatar-placeholder" v-for="i in 3" :key="i"></div>
        </div>
        <span class="social-proof-text">Trusted by structural engineers across India</span>
      </div>
      </div>

      <!-- Right Column: PDF Report Showcase -->
      <div class="hero-showcase-col">
        <pdf-showcase />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTheme } from 'vuetify'
import { useRouter } from 'vue-router'
import SeButton from '@/components/SEButton.vue'
import PdfShowcase from '@/components/homepage/PdfShowcase.vue'

// Emits
interface Emits {
  (e: 'scrollToCalculators'): void
}

defineEmits<Emits>()

// Router
const router = useRouter()

// Theme detection
const theme = useTheme()
const isDarkTheme = computed(() => theme.global.name.value === 'dark')

const navigateToCalculators = () => {
  router.push('/calculators')
}

// Navigate to slab design
const navigateToSlabDesign = () => {
  router.push('/calculators/concrete/slab-design')
}
</script>

<style scoped>
/* Hero Section */
.hero-section {
  min-height: 75vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: var(--bg-canvas);
  padding: 40px 0 0;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  font-family: var(--font-family-body);
}

.hero-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: var(--z-content);
}

.hero-text-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  order: 1;
  width: 100%;
  min-width: 0; /* Prevent grid blowout on mobile */
}

.hero-showcase-col {
  display: flex;
  align-items: flex-start;
  order: 2;
  min-height: 440px;
  width: 100%;
  min-width: 0;
}

/* Desktop: 40/60 Split */
@media (min-width: 1024px) {
  .hero-grid {
    grid-template-columns: minmax(450px, 1fr) 1.5fr;
    align-items: center;
    gap: 4rem;
  }

  .hero-text-col {
    order: unset;
    align-items: flex-start;
    text-align: left;
  }

  .hero-showcase-col {
    order: unset;
  }
}

/* Badge */
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--accent);
  border: 1px solid var(--accent);
  border-radius: var(--radius-pill);
  font-size: var(--font-size-sm);
  font-weight: 500;
  margin-bottom: 32px;
  color: var(--on-accent);
  margin-top: calc(var(--navbar-clearance, 64px) + 12px);
}

/* Typography */
.hero-title {
  font-size: clamp(1.75rem, 5vw + 0.5rem, 3rem);
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 8px;
  letter-spacing: 0em;
  color: var(--text-primary);
  font-family: var(--font-family-body);
  width: 100%;
  overflow-wrap: break-word;
}

.title-highlight {
  color: var(--accent);
}

.hero-subtitle {
  font-size: clamp(0.9rem, 1.5vw + 0.25rem, 1.05rem);
  color: var(--text-body);
  margin-top: 20px;
  margin-bottom: 36px;
  max-width: 540px;
  line-height: 1.5;
  font-family: var(--font-family-body);
}

@media (max-width: 1023px) {
  .hero-subtitle {
    margin-left: auto;
    margin-right: auto;
    padding: 0 1rem;
  }
}

@media (max-width: 767px) {
  .hero-subtitle {
    margin-top: 16px;
    margin-bottom: 24px;
  }
}

.hero-subtitle-title {
  font-size: clamp(1.5rem, 4vw + 0.5rem, 3rem);
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 16px;
  letter-spacing: -0.02em;
  color: var(--text-primary);
  font-family: var(--font-family-body);
}

@media (max-width: 767px) {
  .hero-title {
    font-size: clamp(2rem, 8vw, 2.5rem);
    margin-bottom: 8px;
    line-height: 1.2;
    white-space: normal;
  }

  .hero-actions {
    margin-bottom: 28px;
  }
}

/* Actions */
.hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  margin-bottom: 48px;
  flex-wrap: wrap;
}

@media (min-width: 1024px) {
  .hero-actions {
    justify-content: flex-start;
  }
}

.hero-cta-wrapper {
  font-size: var(--font-size-md);
}

.secondary-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.hero-actions :deep(.se-btn.secondary-cta-btn.se-set5.se-text) {
  text-transform: none;
  color: var(--text-secondary);
}

.hero-actions :deep(.se-btn.secondary-cta-btn.se-set5.se-text:hover) {
  color: var(--text-primary);
}

/* Social proof — swap in real testimonials when collected */
.social-proof {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
}

@media (min-width: 1024px) {
  .social-proof {
    justify-content: flex-start;
  }
}

.social-proof-avatars {
  display: flex;
}

.avatar-placeholder {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--border-subtle);
  border: 2px solid var(--bg-canvas);
  margin-left: -8px;
}

.avatar-placeholder:first-child {
  margin-left: 0;
}

.social-proof-text {
  font-size: var(--font-size-caption);
  color: var(--text-secondary);
  font-family: var(--font-family-body);
}


/* Mobile Responsiveness */
@media (max-width: 1023px) {
  .hero-showcase-col {
    order: 2;
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  .hero-actions {
    order: 3;
  }
}

@media (max-width: 767px) {
  .hero-section {
    min-height: 90vh;
    padding: 60px 0 40px;
  }

  .hero-grid {
    padding: 0 1rem;
    gap: 2rem;
  }

  .hero-showcase-col {
    min-height: 280px;
  }

  .hero-badge {
    margin-top: var(--navbar-clearance-mobile, 36px) !important;
  }

  .hero-actions {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
